import type { GeoJsonObject } from 'geojson';
import { divIcon, DomEvent, Icon, LatLngBounds, Marker as _Marker, TileLayer as TileLayerEntity } from 'leaflet';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { toasting } from 'src/lib/components/Toast.component';
import { parsePublishTime } from 'src/lib/date/parse-publish-time';
import { fget, fpost } from 'src/lib/fetch';
import {
  selectAreaCoordinates,
  selectIsAllAreasSelected,
  selectSelectedArea,
  selectSelectedRiverBounds,
  selectSelectedRiverNames,
} from 'src/redux/slices/area.slice';
import { openMapDevicePanel, syncMapDevicePanel } from 'src/redux/slices/mapDevicePanels.slice';
import { RootState } from 'src/redux/store';
import './CheckBoxWaterLevel.css';

const staleLimitMs = 30 * 60 * 1000;
const metricLabelWidth = 180;
const allowedViewportPaddingRatio = 0.5;
const allowedZoomOutSteps = 1;
const japanViewportBounds = new LatLngBounds(
  [20, 118],
  [50, 156],
);
const deviceMarkerIconCache = new Map<string, ReturnType<typeof divIcon>>();
const damMarkerIcon = new Icon({
  iconUrl: 'assets/dam.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [16, -16],
  tooltipAnchor: [16, -16],
});

interface MetricLabelPlacement {
  offset: [number, number];
}

interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const rectsOverlap = (left: ScreenRect, right: ScreenRect, gap = 6) =>
  left.left < right.right + gap &&
  left.right > right.left - gap &&
  left.top < right.bottom + gap &&
  left.bottom > right.top - gap;

const createLabelCandidates = (anchorX: number, anchorY: number, width: number, height: number) => {
  const candidates: Array<{ offset: [number, number]; rect: ScreenRect }> = [];
  const addCandidate = (offsetX: number, offsetY: number) => {
    candidates.push({
      offset: [offsetX, offsetY],
      rect: {
        left: anchorX + offsetX - width / 2,
        top: anchorY + offsetY - height,
        right: anchorX + offsetX + width / 2,
        bottom: anchorY + offsetY,
      },
    });
  };

  for (let ring = 0; ring < 4; ring += 1) {
    const horizontalDistance = 26 + width / 2 + ring * (width + 10);
    const verticalDistance = 44 + ring * (height + 10);
    const cornerDistance = 24 + ring * (Math.max(width, height) * 0.72 + 10);

    addCandidate(0, -verticalDistance);
    addCandidate(horizontalDistance, height / 2);
    addCandidate(-horizontalDistance, height / 2);
    addCandidate(0, height + 22 + ring * (height + 10));
    addCandidate(width / 2 + cornerDistance, -cornerDistance);
    addCandidate(-(width / 2 + cornerDistance), -cornerDistance);
    addCandidate(width / 2 + cornerDistance, height + cornerDistance);
    addCandidate(-(width / 2 + cornerDistance), height + cornerDistance);
  }

  return candidates;
};

const parseCoordinate = (value?: string): [number, number] | null => {
  if (!value) return null;

  const [lat, lng] = value.split(',').map(Number);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const isStaleMarker = (publish?: string) => {
  const parsed = parsePublishTime(publish);
  if (!parsed) return true;
  return Date.now() - parsed.getTime() >= staleLimitMs;
};

const createDeviceMarkerIcon = (isSelected: boolean, isStale: boolean) => {
  const cacheKey = isSelected ? 'selected' : isStale ? 'stale' : 'active';
  const cachedIcon = deviceMarkerIconCache.get(cacheKey);
  if (cachedIcon) return cachedIcon;

  const color = isSelected ? '#16a34a' : isStale ? '#dc2626' : '#1d4ed8';

  const icon = divIcon({
    className: '',
    iconSize: [34, 50],
    iconAnchor: [17, 50],
    popupAnchor: [0, -42],
    html: `
      <div style="width:34px;height:50px;filter:drop-shadow(0 5px 10px rgba(15,23,42,.25));">
        <svg width="34" height="50" viewBox="0 0 34 50" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M17 1C8.16 1 1 8.16 1 17c0 11.16 11.98 26.95 15.13 30.9a1.09 1.09 0 0 0 1.74 0C21.02 43.95 33 28.16 33 17 33 8.16 25.84 1 17 1Z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
          <circle cx="17" cy="17" r="4.8" fill="#ffffff"/>
        </svg>
      </div>
    `,
  });
  deviceMarkerIconCache.set(cacheKey, icon);
  return icon;
};

export const CheckBoxWaterLevelMarkers = React.forwardRef(
  (
    {
      label = '水位',
      baseTileLayer,
      checked,
    }: {
      label?: string;
      baseTileLayer: React.RefObject<TileLayerEntity>;
      checked?: boolean;
    },
    ref: React.Ref<{ setGeoData?: (data: GeoJsonObject) => void }>,
  ) => {
    const map = useMap();
    const configuredMinZoom = useRef(map.getMinZoom());
    const dispatch = useDispatch();
    // Handle checkbox
    const eLabel = useRef<HTMLLabelElement>(null);
    const eInputLabel = useRef<HTMLDivElement>(null);

    const markersRef = useRef(new Map<string, _Marker>());

    const [position, setPosition] = useState<{
      data?: {
        marker: [number, number];
        pos: string;
        waterLevel: string;
        flowRate: string;
        deviceId: string;
        velocity: string;
        publish: string;
        areaName: string;
        riverName: string;
      }[];
    }>({});
    const [dataDam, setDam] = useState<any>([]);

    const getDamData = async () => {
      try {
        const res = await fget('api/dams');
        const items = res?.[1]?.Items || [];
        const dams: any = [];
        items.forEach((item: any) => {
          const coordinate = parseCoordinate(item?.DAM_COORDINATE?.S);
          if (!coordinate) return;

          dams.push({
            marker: coordinate,
            DAM_NUMBER: item.DAM_NUMBER?.S ? item.DAM_NUMBER.S : '',
            RIVER_NAME: item.RIVER_NAME?.S ? item.RIVER_NAME.S : '',
            DAM_AREA: item.DAM_AREA?.S ? item.DAM_AREA.S : '',
            DAM_ID: item.DAM_ID?.S || '',
          });
        });

        setDam(dams);
      } catch (e) {
        console.log(e);
        toasting({ children: 'データの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
      }
    };

    const getDeviceData = async () => {
      try {
        const res = await fget('api/devices?status=ACTIVE');
        const items = res?.[1]?.Items || [];
        const positions: any = [];

        for (const item of items) {
          const deviceId = item?.DEVICE_ID?.S;
          const coordinate = parseCoordinate(item?.DEVICE_COORDINATE?.S);
          if (!deviceId || !coordinate) continue;

          const query = {
            query: {
              ExpressionAttributeValues: {
                ':id': { S: deviceId },
              },
              KeyConditionExpression: 'DEVICE_ID = :id',
              TableName: 'DEVICE_RECORD',
              ScanIndexForward: false,
              Limit: 1,
            },
          };
          const data = await fpost<any>('api/device/record/query', {
            body: query,
          });

          const record = data?.[1]?.Items?.[0];

          positions.push({
            marker: coordinate,
            pos: item.DEVICE_NAME?.S ? item.DEVICE_NAME.S : '',
            waterLevel: record?.WATER_LEVEL?.S || '0',
            flowRate: record?.FLOW_RATE?.S || '0',
            deviceId,
            velocity: record?.VELOCITY?.S || '0',
            publish: record?.Publish?.S || record?.PUBLISH?.S || record?.PUBLISH_FORMATED?.S || '',
            areaName: item.AREA_NAME?.S || '',
            riverName: item.RIVER_NAME?.S || '',
          });
        }

        setPosition({ data: positions });
        positions.forEach((position: any) => dispatch(syncMapDevicePanel(position)));
      } catch (e) {
        console.log(e);
        toasting({ children: 'データの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        await Promise.all([getDamData(), getDeviceData()]);
      };
      fetchData();
      // Call getDeviceData every 30 seconds
      const intervalId =  setInterval( async () =>  {
        await Promise.all([ getDeviceData()]);
      }, 30000);
      return () => {
        clearInterval(intervalId);
      };
    }, []);

    const [inputChecked, setInputChecked] = useState<boolean>(!!checked);
    const [flowRateChecked, setFlowChecked] = useState<boolean>(false);
    const [velocityChecked, setVelocityChecked] = useState<boolean>(false);
    const [labelPlacements, setLabelPlacements] = useState<Record<string, MetricLabelPlacement>>({});

    const selectedDeviceIds = useSelector((state: RootState) =>
      state.mapDevicePanelsReducer.panels.map((panel) => panel.deviceId),
    );
    const selectedArea = useSelector(selectSelectedArea);
    const isAllAreasSelected = useSelector(selectIsAllAreasSelected);
    const selectedAreaCoordinates = useSelector(selectAreaCoordinates);
    const selectedRiverNames = useSelector(selectSelectedRiverNames);
    const selectedRiverBounds = useSelector(selectSelectedRiverBounds);
    const lastAutoFitKey = useRef('');

    const selectedAreaName = selectedArea?.AREA_NAME?.S || '';
    const selectedRiverNameSet = useMemo(() => new Set(selectedRiverNames), [selectedRiverNames]);
    const filteredDams = useMemo(
      () =>
        dataDam.filter(
          (dam: any) =>
            !selectedAreaName ||
            dam.DAM_AREA === selectedAreaName ||
            (dam.RIVER_NAME && selectedRiverNameSet.has(dam.RIVER_NAME)),
        ),
      [dataDam, selectedAreaName, selectedRiverNameSet],
    );
    const filteredPositions = useMemo(
      () =>
        (position.data || []).filter(
          (device) =>
            !selectedAreaName ||
            device.areaName === selectedAreaName ||
            (device.riverName && selectedRiverNameSet.has(device.riverName)),
        ),
      [position.data, selectedAreaName, selectedRiverNameSet],
    );

    useEffect(() => {
      if (!inputChecked && !flowRateChecked && !velocityChecked) {
        setLabelPlacements({});
        return;
      }

      const updateLabelPlacements = () => {
        const mapSize = map.getSize();
        const metricCount = Number(inputChecked) + Number(flowRateChecked) + Number(velocityChecked);
        const labelHeight = 38 + metricCount * 20;
        const rightInset = document.body.classList.contains('map-right-menu-open') ? 260 : 8;
        const viewportRect: ScreenRect = {
          left: 8,
          top: 8,
          right: mapSize.x - rightInset,
          bottom: mapSize.y - 8,
        };
        const occupiedRects: ScreenRect[] = [];
        const nextPlacements: Record<string, MetricLabelPlacement> = {};
        const sortedPositions = [...filteredPositions].sort((left, right) => {
          const leftPoint = map.latLngToContainerPoint(left.marker);
          const rightPoint = map.latLngToContainerPoint(right.marker);
          return leftPoint.y - rightPoint.y || leftPoint.x - rightPoint.x;
        });

        sortedPositions.forEach((device) => {
          const anchor = map.latLngToContainerPoint(device.marker);
          const candidates = createLabelCandidates(anchor.x, anchor.y, metricLabelWidth, labelHeight);
          const availableCandidate = candidates.find(
            (candidate) =>
              candidate.rect.left >= viewportRect.left &&
              candidate.rect.right <= viewportRect.right &&
              candidate.rect.top >= viewportRect.top &&
              candidate.rect.bottom <= viewportRect.bottom &&
              occupiedRects.every((occupiedRect) => !rectsOverlap(candidate.rect, occupiedRect)),
          );
          const fallbackCandidate =
            availableCandidate ??
            candidates
              .filter(
                (candidate) =>
                  candidate.rect.right >= viewportRect.left &&
                  candidate.rect.left <= viewportRect.right &&
                  candidate.rect.bottom >= viewportRect.top &&
                  candidate.rect.top <= viewportRect.bottom,
              )
              .sort((left, right) => {
                const overlapCount = (candidate: ScreenRect) =>
                  occupiedRects.filter((occupiedRect) => rectsOverlap(candidate, occupiedRect)).length;
                return overlapCount(left.rect) - overlapCount(right.rect);
              })[0] ?? candidates[0];

          occupiedRects.push(fallbackCandidate.rect);
          nextPlacements[device.deviceId] = {
            offset: fallbackCandidate.offset,
          };
        });

        setLabelPlacements(nextPlacements);
      };

      const frameId = window.requestAnimationFrame(updateLabelPlacements);
      map.on('zoomend moveend resize', updateLabelPlacements);
      return () => {
        window.cancelAnimationFrame(frameId);
        map.off('zoomend moveend resize', updateLabelPlacements);
      };
    }, [filteredPositions, flowRateChecked, inputChecked, map, velocityChecked]);

    useEffect(() => {
      const resetViewportLimits = (minZoom = configuredMinZoom.current) => {
        map.setMaxBounds(new LatLngBounds([]));
        map.setMinZoom(minZoom);
      };

      if (!selectedAreaName && !isAllAreasSelected) {
        resetViewportLimits();
        lastAutoFitKey.current = '';
        return;
      }

      const riverBoundsKey = selectedRiverBounds?.flat().join(',') || '';
      const markerKey = filteredPositions
        .map((device) => `${device.deviceId}:${device.marker.join(',')}`)
        .join('|');
      const damKey = filteredDams.map((dam: any) => `${dam.DAM_ID}:${dam.marker.join(',')}`).join('|');
      const autoFitKey = [isAllAreasSelected ? 'ALL' : selectedAreaName, riverBoundsKey, markerKey, damKey].join('|');
      if (lastAutoFitKey.current === autoFitKey) return;

      const applyViewportLimits = () => {
        const fittedZoom = map.getZoom();
        const allowedMinZoom = Math.max(configuredMinZoom.current, fittedZoom - allowedZoomOutSteps);
        const allowedBounds = map.getBounds().pad(allowedViewportPaddingRatio);

        map.setMinZoom(allowedMinZoom);
        map.setMaxBounds(allowedBounds);
      };

      if (isAllAreasSelected) {
        resetViewportLimits();
        map.setMaxBounds(japanViewportBounds);
      }

      const bounds = new LatLngBounds([]);
      if (selectedRiverBounds) {
        bounds.extend(selectedRiverBounds[0]);
        bounds.extend(selectedRiverBounds[1]);
      }
      filteredPositions.forEach((device) => bounds.extend(device.marker));
      filteredDams.forEach((dam: any) => bounds.extend(dam.marker));

      if (bounds.isValid()) {
        resetViewportLimits();
        map.fitBounds(bounds, {
          paddingTopLeft: [24, 24],
          paddingBottomRight: [264, 48],
          maxZoom: 13,
          animate: false,
        });
        if (isAllAreasSelected) {
          map.setMaxBounds(japanViewportBounds);
        } else {
          applyViewportLimits();
        }
        lastAutoFitKey.current = autoFitKey;
        return;
      }

      const [lat, lng] = selectedAreaCoordinates.split(',').map(Number);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        resetViewportLimits();
        map.setView([lat, lng], 12, { animate: false });
        if (isAllAreasSelected) {
          map.setMaxBounds(japanViewportBounds);
        } else {
          applyViewportLimits();
        }
        lastAutoFitKey.current = autoFitKey;
      }
    }, [
      filteredDams,
      filteredPositions,
      isAllAreasSelected,
      map,
      selectedAreaCoordinates,
      selectedAreaName,
      selectedRiverBounds,
    ]);

    // Handle ref
    useImperativeHandle(ref, () => ({}));

    return (
      <>
        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={'flow_rate'}
            checked={flowRateChecked}
            onChange={(e) => {
              setFlowChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={'flow_rate'}>
            流量
          </label>
        </div>

        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={label}
            checked={inputChecked}
            onChange={(e) => {
              setInputChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={label} ref={eLabel}>
            {label}
          </label>
        </div>

        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={'velocity'}
            checked={velocityChecked}
            onChange={(e) => {
              setVelocityChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={'velocity'} ref={eLabel}>
            {'流速'}
          </label>
        </div>

        {(inputChecked || flowRateChecked || velocityChecked) &&
          filteredDams.map((position: any, index: number) => (
            <Marker
              key={`dam-marker-${position.DAM_ID || index}`}
              position={position.marker}
              icon={damMarkerIcon}
              ref={(e) => {
                if (e) markersRef.current.set(position.deviceId, e);
              }}>
              <Tooltip sticky className="device-dam-tooltip">
                ダム:{position.DAM_NUMBER}
              </Tooltip>
            </Marker>
          ))}

        {
          filteredPositions.map((position) => (
            <Marker
              eventHandlers={{
                click: (event) => {
                  if (event.originalEvent) DomEvent.stopPropagation(event.originalEvent);
                  dispatch(openMapDevicePanel(position));
                },
              }}
              ref={(e) => {
                if (e) markersRef.current.set(position.deviceId, e);
              }}
              icon={createDeviceMarkerIcon(
                selectedDeviceIds.includes(position.deviceId),
                !selectedDeviceIds.includes(position.deviceId) && isStaleMarker(position.publish),
              )}
              zIndexOffset={selectedDeviceIds.includes(position.deviceId) ? 1200 : 900}
              key={`marker-${position.deviceId}`}
              position={position.marker}>
              {(inputChecked || flowRateChecked || velocityChecked) && (
                <Tooltip
                  direction="top"
                  offset={labelPlacements[position.deviceId]?.offset ?? [0, -44]}
                  opacity={1}
                  permanent
                  interactive
                  eventHandlers={{
                    click: (event) => {
                      if (event.originalEvent) DomEvent.stopPropagation(event.originalEvent);
                      dispatch(openMapDevicePanel(position));
                    },
                  }}
                  className="device-metric-tooltip is-compact">
                  <div className="device-metric-tooltipContent">
                    <strong className="device-metric-name">{position.pos}</strong>
                    <div className="device-metric-values">
                      {flowRateChecked && (
                        <span className="device-metric-value device-metric-flowRate">
                          <span>流量</span>
                          <strong>{parseFloat(position.flowRate || '0').toFixed(2)} m³/s</strong>
                        </span>
                      )}
                      {inputChecked && (
                        <span className="device-metric-value device-metric-waterLevel">
                          <span>水位</span>
                          <strong>{parseFloat(position.waterLevel || '0').toFixed(2)} m</strong>
                        </span>
                      )}
                      {velocityChecked && (
                        <span className="device-metric-value device-metric-velocity">
                          <span>流速</span>
                          <strong>{parseFloat(position.velocity || '0').toFixed(2)} m/s</strong>
                        </span>
                      )}
                    </div>
                    <small className="device-metric-updated">更新日時: {position.publish}</small>
                  </div>
                </Tooltip>
              )}
            </Marker>
          ))}
      </>
    );
  },
);
